import React from 'react';
import { Helmet } from 'react-helmet-async';

const HipaaNPP = () => {
  return (
    <>
      <Helmet>
        <title>HIPAA Notice of Privacy Practices | Fort Worth Dental</title>
        <meta name="description" content="Read Fort Worth Dental's HIPAA Notice of Privacy Practices. Learn how we protect and use your protected health information (PHI)." />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">HIPAA Notice of Privacy Practices</h1>
            <p className="text-lg">Effective Date: January 1, 2024</p>
          </div>
        </section>

        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW YOU CAN GET ACCESS TO THIS INFORMATION. PLEASE REVIEW IT CAREFULLY.</h2>
              
              <p className="text-gray-700 mb-4">
                Fort Worth Dental is committed to protecting the privacy of your health information. This Notice of Privacy Practices ("Notice") describes how we may use and disclose your protected health information ("PHI") to carry out treatment, payment, or health care operations, and for other purposes that are permitted or required by law.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Our Legal Duty</h3>
              <p className="text-gray-700 mb-4">
                We are required by applicable federal and state law to maintain the privacy of your PHI. We are also required to give you this Notice about our privacy practices, our legal duties, and your rights concerning your PHI. We must follow the privacy practices that are described in this Notice while it is in effect.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Uses and Disclosures of Protected Health Information</h3>
              
              <h4 className="text-lg font-semibold text-gray-700 mb-2">Treatment</h4>
              <p className="text-gray-700 mb-4">
                We may use and disclose your PHI to provide, coordinate, or manage your dental care and any related services. For example, we may disclose your PHI to a specialist to whom we refer you for treatment.
              </p>

              <h4 className="text-lg font-semibold text-gray-700 mb-2">Payment</h4>
              <p className="text-gray-700 mb-4">
                We may use and disclose your PHI to obtain payment for services we provide to you. For example, we may send claims to your dental plan or insurance company.
              </p>

              <h4 className="text-lg font-semibold text-gray-700 mb-2">Health Care Operations</h4>
              <p className="text-gray-700 mb-4">
                We may use and disclose your PHI for our health care operations. These uses and disclosures are necessary to run the practice and make sure that all of our patients receive quality care.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Your Rights Regarding Your PHI</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li><strong>Right to Access:</strong> You have the right to look at or get copies of your PHI.</li>
                <li><strong>Right to Amend:</strong> You have the right to request that we amend your PHI if you believe it is incorrect or incomplete.</li>
                <li><strong>Right to Request Restrictions:</strong> You have the right to request restrictions on certain uses and disclosures of your PHI.</li>
                <li><strong>Right to Request Confidential Communications:</strong> You have the right to request that we communicate with you about your PHI in a certain way or at a certain location.</li>
                <li><strong>Right to an Accounting of Disclosures:</strong> You have the right to receive a list of disclosures we have made of your PHI.</li>
                <li><strong>Right to a Paper Copy of This Notice:</strong> You have the right to receive a paper copy of this Notice at any time.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Changes to This Notice</h3>
              <p className="text-gray-700 mb-4">
                We reserve the right to change the terms of this Notice at any time. Any new Notice will be effective for all PHI that we maintain at that time. We will post the current Notice in our office and on our website.
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Complaints</h3>
              <p className="text-gray-700 mb-4">
                If you believe your privacy rights have been violated, you may file a complaint with our office or with the Secretary of the Department of Health and Human Services. To file a complaint with our office, contact our Privacy Officer at:
              </p>
              <div className="bg-gray-100 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Fort Worth Dental</strong></p>
                <p className="text-gray-700">4620 Citylake Blvd W</p>
                <p className="text-gray-700">Fort Worth, TX 76132</p>
                <p className="text-gray-700">Phone: (817) 985-3337</p>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-xl font-bold text-gray-800 mb-3">Contact Information</h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about this Notice or our privacy practices, please contact our Privacy Officer at the address or phone number listed above.
              </p>
            </section>

            {/* Acknowledgment Form */}
            <section className="border-t border-gray-300 pt-8 mt-8">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Acknowledgment of Receipt</h3>
              <p className="text-gray-700 mb-4">
                By signing below, I acknowledge that I have received a copy of Fort Worth Dental's Notice of Privacy Practices.
              </p>
              <div className="bg-gray-100 p-6 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Patient Name (Print)</label>
                    <div className="border-b-2 border-gray-400 h-8"></div>
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2">Date</label>
                    <div className="border-b-2 border-gray-400 h-8"></div>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2">Signature</label>
                  <div className="border-b-2 border-gray-400 h-8"></div>
                </div>
                <p className="text-sm text-gray-600 italic">
                  If signed by a personal representative, describe authority: ___________________________
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                <strong>Note:</strong> You may download and print this page for your records, or request a physical copy from our office.
              </p>
            </section>

          </div>
        </div>
      </div>
    </>
  );
};

export default HipaaNPP;
